/**
 * 공유 링크 — 원문 주소가 아니라 우리 공개 페이지(`/share/media/{ad|post|product}/{id}?q=`)를
 * 건넨다. 비회원도 열 수 있다.
 *
 * 🚨 **상품은 주소에 몰을 함께 싣는다**(`{몰}~{상품 id}`) — 상품 id 는 몰 안에서만 유일해서
 *   몰을 빼면 같은 id 를 가진 **남의 몰 상품**이 열린다.
 * ⚠️ 가름표는 `~` 다 — 몰 코드에도 상품 id 에도 안 나오는 글자라야 한 번에 쪼개진다
 *   (`-`·`_` 는 상품 id 에 흔하다).
 */

import { PRODUCT_SOURCE_LABELS } from 'src/constants/product-sources';

import type { MediaCard } from './search-all-media-cards';

/** 공유 페이지 경로의 앞부분 */
export const SHARE_MEDIA_ROOT = '/share/media';

/** 검색어 길이 상한 — 공유 페이지가 이보다 긴 검색어는 버린다 */
export const SHARE_QUERY_MAX = 60;

/** 상품 주소의 몰·id 가름표 — 몰 코드에도 상품 id 에도 안 나오는 글자 */
export const SHARE_PRODUCT_SEPARATOR = '~';

/**
 * 이 소재를 **공유할 수 있나** — 주소를 만들 수 있으면 참.
 * 🚨 가리는 자리는 여기 하나다 — 단추 쪽에서 갈래를 또 세면 둘이 어긋난다(상품 북마크가 그랬다).
 */
export function canShareMedia(card: MediaCard): boolean {
  return toMediaSharePath(card, '') !== '';
}

/**
 * @param card 공유할 카드
 * @param query 지금 검색어 — 공유 페이지 뒤에 깔 검색 결과
 * @returns 경로(`/share/media/…`) — 주소를 만들 수 없으면 빈 문자열
 */
export function toMediaSharePath(card: MediaCard, query: string): string {
  const q = query.trim().slice(0, SHARE_QUERY_MAX);
  const tail = q ? `?q=${encodeURIComponent(q)}` : '';

  if (card.kind === 'product') {
    const source = String(card.product?.source ?? '').toLowerCase();
    const postId = String(card.product?.postId ?? '');
    // ⚠️ 몰을 모르면 주소를 만들지 않는다 — 몰 없는 상품 주소는 남의 몰을 열 수 있다
    if (!postId || !PRODUCT_SOURCE_LABELS[source]) return '';
    return `${SHARE_MEDIA_ROOT}/product/${encodeURIComponent(`${source}${SHARE_PRODUCT_SEPARATOR}${postId}`)}${tail}`;
  }

  const id = card.kind === 'ad' ? String(card.ad?.id ?? '') : String(card.post?.id ?? '');
  if (!id) return '';
  return `${SHARE_MEDIA_ROOT}/${card.kind === 'ad' ? 'ad' : 'post'}/${encodeURIComponent(id)}${tail}`;
}

/**
 * 공유 페이지 주소 조각 거르기.
 *
 * 🚨 **모양을 여기서 막는다** — 이 뒤는 ES 질의다. 거르지 않으면 주소가 질의로 그대로 간다.
 * @returns 모양이 맞지 않으면 `null` — 광고 id 는 숫자, 게시물 id 는 `채널-영숫자`,
 *   상품은 `{아는 몰}~{영숫자·-·_}`(몰이 목록에 없으면 거른다)
 */
export function readShareMediaParams(
  kind: string,
  id: string,
  q: string | undefined,
): { kind: 'ad' | 'post'; id: string; q: string } | { kind: 'product'; id: string; source: string; q: string } | null {
  const decoded = decodeURIComponent(id);
  const query = (q ?? '').trim().slice(0, SHARE_QUERY_MAX);

  if (kind === 'ad' && /^\d{5,25}$/.test(decoded)) return { kind, id: decoded, q: query };
  if (kind === 'post' && /^[a-z]+-[\w-]{1,120}$/i.test(decoded)) return { kind, id: decoded, q: query };

  if (kind === 'product') {
    const at = decoded.indexOf(SHARE_PRODUCT_SEPARATOR);
    if (at <= 0) return null;
    const source = decoded.slice(0, at).toLowerCase();
    const postId = decoded.slice(at + 1);
    if (!PRODUCT_SOURCE_LABELS[source]) return null;
    if (!/^[\w-]{1,120}$/.test(postId)) return null;
    return { kind, id: postId, source, q: query };
  }

  return null;
}
